import { ValueModel } from '@kts-front/models';
import { BaseResponse, Nullable } from '@kts-front/types';
import { IReactionDisposer, reaction } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { LeadListEntityApi } from 'entities/Lead';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { OptionEntity } from 'types/OptionEntity';
import { isDefined } from 'utils/isDefined';

import { FieldSelectStore, FieldSelectStoreOptions } from '../form/FieldSelectStore';

type FieldSelectClientLeadStoreOptions = Omit<FieldSelectStoreOptions<OptionEntity>, 'getOptions'> & {
  filterByUniqueLeads?: boolean;
};

export class FieldSelectClientLeadStore extends FieldSelectStore {
  private readonly _loadLeadsRequest = apiStore.createRequest({
    url: ENDPOINTS.lead.list,
  });

  readonly clientId = new ValueModel<Nullable<number>>(null);
  readonly projectID = new ValueModel<Nullable<number>>(null);

  private readonly _disposers: IReactionDisposer[];

  private readonly _filterByUniqueLeads: boolean;

  constructor({ filterByUniqueLeads = true, ...options }: FieldSelectClientLeadStoreOptions) {
    super({
      ...options,
      getOptions: async ({ search }): Promise<BaseResponse<{ items: OptionEntity[]; total: number }>> => {
        if (!isDefined(this.clientId.value)) {
          return {
            isError: false,
            data: {
              items: [],
              total: 0,
            },
          };
        }

        const requestPayload: Record<string, unknown> = {
          client_id__in: this.clientId.value,
          search: search || undefined,
        };

        if (this._filterByUniqueLeads) {
          requestPayload.status__in = 'unique';
        }

        const response = await this._loadLeadsRequest.call<LeadListEntityApi>({
          params: requestPayload,
        });

        if (response.isError) {
          logApiErrWithSentry({
            endpoint: ENDPOINTS.lead.list,
            method: 'GET',
            requestPayload,
            response,
          });

          return {
            isError: true,
          };
        }

        // Сохраняем данные для использования в reaction
        this._lastResponseData = response.data;

        const leads = response.data.items.map((lead) => {
          const parts = [lead.id.toString()];

          if (lead.project?.name) {
            parts.push(lead.project.name);
          }

          if (lead.place_type?.name) {
            parts.push(lead.place_type.name);
          }

          return {
            key: lead.id.toString(),
            value: parts.join(' • '),
          };
        });

        // Устанавливаем projectID только если ровно один элемент
        if (response.data.items.length === 1 && response.data.items[0].project?.id) {
          this.projectID.change(response.data.items[0].project.id);
        }

        return {
          isError: false,
          data: {
            items: leads,
            total: response.data.total,
          },
        };
      },
    });

    this._filterByUniqueLeads = filterByUniqueLeads;

    this._disposers = [
      // Автоматически выбираем первое значение после загрузки опций если оно одно
      reaction(
        () => this.options,
        (options) => {
          if (options.length === 1 && this.value.length === 0) {
            this.setValue([options[0]]);
          }
        },
      ),

      // Обновляем projectID при выборе заявки
      reaction(
        () => this.value[0]?.key,
        (selectedLeadId) => {
          if (selectedLeadId && this._lastResponseData) {
            const selectedLead = this._lastResponseData.items.find((lead) => lead.id.toString() === selectedLeadId);

            if (selectedLead && selectedLead.project?.id) {
              this.projectID.change(selectedLead.project.id);
            }
          }
        },
      ),
    ];
  }

  private _lastResponseData: LeadListEntityApi | null = null;

  destroy(): void {
    super.destroy();
    this._disposers.forEach((d) => d());
  }
}
